import { Modal } from "./Modal";
import { Logo } from "./Logo";
import { EXPLORADOR } from "../lib/enlaces";
import { useT } from "../lib/i18n";
import type { RedKeel } from "../config/networks";

/**
 * Modal de proceso GENÉRICO, hermano de `ModalProceso`. Ese está atado al `Progreso` de las acciones
 * de un vault (nombre → tabla de textos); este se alimenta de un estado libre, para dar el MISMO
 * feedback visual (logo Keel «cargando» → ✓ éxito / ✗ error) a los flujos que no son acciones de
 * vault: crear una piscina, el puente entre cadenas y el gobierno (timelock). Reutiliza las mismas
 * clases CSS (`.proceso`, `.proceso-logo`, `.proceso-check`, `.proceso-x`), así que se ve idéntico.
 *
 * Soporta multi-paso (`pasos`) para procesos con varias firmas encadenadas (crear = crea → aprueba →
 * deposita → abre): mientras carga, muestra en qué paso va.
 */
export type PasoOp = { etiqueta: string; estado: "espera" | "haciendo" | "hecho" | "fallo" };

export type EstadoOp = {
  fase: "cargando" | "ok" | "error";
  /** Título: el gerundio mientras carga («Creando la piscina») o el título de éxito. */
  titulo: string;
  /** Subtítulo: el paso actual mientras carga, o el detalle del éxito. */
  sub?: string;
  /** Mensaje cuando `fase === "error"`. */
  mensaje?: string;
  hash?: string;
  /** URL directa a la tx en el explorador. Si se pasa, gana al enlace derivado de `red`+`hash`
   *  (el puente cruza cadenas: su explorador va por id de cadena, no por `red.clave`). */
  txUrl?: string;
  /** Pasos encadenados, opcional (solo crear los usa). */
  pasos?: PasoOp[];
};

const ICONO_PASO: Record<PasoOp["estado"], string> = {
  hecho: "✓",
  fallo: "!",
  haciendo: "•",
  espera: "·",
};

export function ModalOperacion({
  estado, cerrar, red,
}: {
  estado: EstadoOp | null;
  cerrar: () => void;
  /** Red para el enlace al explorador; si no se pasa, no se muestra el enlace de la tx. */
  red?: RedKeel;
}) {
  const t = useT();
  if (!estado) return null;
  const cargando = estado.fase === "cargando";
  const tx = estado.txUrl ?? (estado.hash && red ? `${EXPLORADOR[red.clave]}/tx/${estado.hash}` : null);
  const titulo = cargando ? t("En marcha") : estado.fase === "ok" ? estado.titulo : t("No se pudo completar");

  return (
    <Modal
      abierto
      cerrar={cerrar}
      titulo={titulo}
      ancho={440}
      pie={
        cargando ? undefined : (
          <button className="btn principal" style={{ marginLeft: "auto" }} onClick={cerrar} data-foco>
            {t("Listo")}
          </button>
        )
      }
    >
      {cargando ? (
        <div className="proceso proceso-cargando">
          <div className="proceso-logo" aria-hidden><Logo size={52} /></div>
          <h3>{estado.titulo}…</h3>
          {estado.sub && <p className="detalle">{estado.sub}</p>}
          {estado.pasos && estado.pasos.length > 0 && (
            <ul className="proceso-pasos">
              {estado.pasos.map((p, i) => (
                <li key={i} className={`proceso-paso pp-${p.estado}`}>
                  <span className="proceso-paso-ic" aria-hidden>{ICONO_PASO[p.estado]}</span>
                  <span>{p.etiqueta}</span>
                </li>
              ))}
            </ul>
          )}
          {tx && (
            <a className="proceso-tx" href={tx} target="_blank" rel="noreferrer">
              {t("Ver transacción en el explorador ↗")}
            </a>
          )}
        </div>
      ) : estado.fase === "ok" ? (
        <div className="proceso proceso-ok">
          <div className="proceso-check" aria-hidden>✓</div>
          <h3>{estado.titulo}</h3>
          {estado.sub && <p className="detalle">{estado.sub}</p>}
          {tx && (
            <a className="proceso-tx" href={tx} target="_blank" rel="noreferrer">
              {t("Ver transacción en el explorador ↗")}
            </a>
          )}
        </div>
      ) : (
        <div className="proceso proceso-error">
          <div className="proceso-x" aria-hidden>!</div>
          <h3>{t("No se pudo completar")}</h3>
          <p className="detalle proceso-mensaje-error">{estado.mensaje}</p>
        </div>
      )}
    </Modal>
  );
}
